import React, { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExternalLink } from "ui/Link";
import { searchNewHashtag } from "actions/sourceMedia";
import { SELECT_NEW_HASHTAG_ROUTINE } from "constants/ActionTypes";
import { WEEKLY_HASHTAG_SEARCH_MAX } from "constants/MediaLibrary";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { Spinner } from "@/components/ui/spinner";
import type { ReduxRootState } from "types/redux-store";

interface Account {
  _id: string;
}

interface AddHashtagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hashtagName: string;
  account: Account;
  activeHashtagsCount: number;
  /** Dialog title - defaults to "Add Hashtag" */
  title?: string;
  /** Callback after successful add - useful for clearing input fields */
  onSuccess?: () => void;
}

/**
 * Modern dialog for adding/reactivating hashtags.
 * Used by both ActiveHashtags (add new) and ExpiredHashtags (reactivate).
 */
export const AddHashtagDialog: React.FC<AddHashtagDialogProps> = ({
  open,
  onOpenChange,
  hashtagName,
  account,
  activeHashtagsCount,
  title = "Add Hashtag",
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const isFetching = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [SELECT_NEW_HASHTAG_ROUTINE])
  );

  // Track if we've started saving to detect completion
  const wasSavingRef = useRef(false);

  // Close dialog when save completes successfully
  useEffect(() => {
    if (wasSavingRef.current && !isFetching) {
      onSuccess?.();
      onOpenChange(false);
    }
    wasSavingRef.current = isFetching;
  }, [isFetching, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleConfirm = useCallback(() => {
    if (hashtagName && account) {
      dispatch(
        searchNewHashtag(account._id, hashtagName.toLowerCase(), "library")
      );
    }
  }, [dispatch, hashtagName, account]);

  const atWeeklyMax = activeHashtagsCount >= WEEKLY_HASHTAG_SEARCH_MAX;
  const canConfirm =
    !atWeeklyMax && !isFetching && hashtagName.trim().length > 0;

  // Clean hashtag name (remove leading #)
  const cleanHashtagName = hashtagName.startsWith("#")
    ? hashtagName.slice(1)
    : hashtagName;

  if (!cleanHashtagName) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-base font-semibold text-neutral-900">
              {title}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              Instagram limits users to {WEEKLY_HASHTAG_SEARCH_MAX} hashtags in
              a rolling 7 day period, so please check that it&apos;s the right
              tag.
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            {/* Preview link */}
            <ExternalLink
              href={`https://www.instagram.com/explore/tags/${cleanHashtagName}/`}
            >
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors">
                Preview #{cleanHashtagName} on Instagram
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
              </span>
            </ExternalLink>

            {/* Warning if at max */}
            {atWeeklyMax && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-inter text-sm text-red-700">
                  You have reached Instagram&apos;s weekly max of{" "}
                  {WEEKLY_HASHTAG_SEARCH_MAX} searches. You will need to wait
                  for the next active hashtag to expire before you can add this
                  hashtag.
                </p>
              </div>
            )}

            {/* Confirmation question */}
            {!atWeeklyMax && (
              <p className="font-inter text-sm text-neutral-700">
                Add{" "}
                <span className="font-semibold text-blue-600">
                  #{cleanHashtagName}
                </span>{" "}
                to your active hashtag searches for 7 days?
              </p>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              variant="blue-primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {isFetching ? (
                <>
                  <Spinner size="sm" variant="inverted" />
                  Adding...
                </>
              ) : (
                `Add #${cleanHashtagName}`
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddHashtagDialog;
